import React, { ReactNode, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { Button, ButtonStyles, CardRow, Cell, Icon, Row, StatusPayment, Table, TableTimestamp, Text, TooltipIcon } from 'components';
import { theme, toRem } from 'design';
import { PaymentMethodAccountNumber } from 'forms/PaymentMethodForm/styles';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { CardType, IAlert, PayoutStatus } from 'types';
import { capitalize } from 'utils/strings';

export const StyledCardRow = styled(CardRow)`
  grid-template-columns: 1fr;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

// Payouts
export const EmployeePayoutTable = styled(Table).attrs({
  scrollable: true,
  sticky: true,
  cellMinWidth: 100,
})`
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
  }
`;

// EPT = Employee Payout Table
const EPTRow = styled(Row)`
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: table-row;
  }
`;

interface IEmployeePayoutProps {
  alert?: IAlert;
  amount?: string;
  date?: string;
  employer?: string;
  isLoading?: boolean;
  note?: string;
  paymentMethod?: string;
  paymentMethodType?: CardType;
  status?: PayoutStatus;
  time?: string;
  payoutId?: string;
}

export const EmployeePayoutTableRow = ({ alert, amount, date, employer, isLoading, note, paymentMethod, paymentMethodType, status, time, payoutId, ...rest }: IEmployeePayoutProps) => (
  <EPTRow clickable onClick={() => browserHistory.push(`${routeConstants.private.employeePayoutDetails}/${payoutId}`)} {...rest}>
    <Cell data-cy="payout-timestamp">{isLoading || !date ? <Skeleton width={55} /> : <TableTimestamp align="left" date={date} time={time} isLoading={isLoading} />}</Cell>

    <Cell data-cy="payout-status">
      {isLoading || !status ? (
        <Skeleton width={55} />
      ) : (
        <StatusPayment
          status={status}
          isLoading={isLoading}
          paymentId={payoutId}
          {...(alert && {
            subStatusLabel: alert?.label,
            subStatusTooltip: alert?.message,
          })}
        />
      )}
    </Cell>

    <Cell data-cy="payout-amount" align="right">
      {isLoading ? <Skeleton width={55} /> : <strong>{amount}</strong>}
    </Cell>

    <Cell data-cy="payout-paymentMethod">
      {isLoading ? (
        <Skeleton width={130} />
      ) : status === PayoutStatus.PAYROLL ? (
        capitalize(status)
      ) : (
        <PaymentMethodAccountNumber cardType={paymentMethodType} align="start" cardNumber={paymentMethod} short fontSize="small" fontWeight="regular" />
      )}
    </Cell>

    <Cell data-cy="payout-employer">{isLoading ? <Skeleton width={55} /> : employer}</Cell>

    <Cell data-cy="payout-note" minWidth={300} maxWidth={350} wrapText>
      {isLoading ? <Skeleton width={55} /> : note || '--'}
    </Cell>
  </EPTRow>
);

export const EmployeePayoutList = styled.ul`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(22)};
  list-style: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: none;
  }
`;

// EPC = EmployeePayoutCard
const EPCItemLayout = styled.li`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(11)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.white};
  box-shadow:
    0px 9.38125px 31.4942px rgba(0, 0, 0, 0.0238443),
    0px 5.25905px 17.6554px rgba(0, 0, 0, 0.02),
    0px 2.79304px 9.37664px rgba(0, 0, 0, 0.0161557),
    0px 1.16225px 3.90183px rgba(0, 0, 0, 0.0112458);
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EPCHeaderLayout = styled.div<Partial<IEmployeePayoutProps>>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0 ${toRem(11)};
  transition: background-color ${theme.transition.nav};

  ${({ status }) =>
    status === PayoutStatus.PAID &&
    css`
      background-color: ${theme.color.success.medium};
    `}

  ${({ status }) =>
    status === PayoutStatus.PENDING &&
    css`
      background-color: ${theme.color.warning.medium};
    `}

    ${({ status }) =>
    status === PayoutStatus.EXPIRED &&
    css`
      background-color: ${theme.color.danger.medium};
    `}

    ${({ status }) =>
    status === PayoutStatus.CANCELED &&
    css`
      background-color: ${theme.color.neutral[900]};

      * {
        color: ${theme.color.white};
      }
    `}

      ${({ isLoading }) =>
    isLoading &&
    css`
      background-color: ${theme.color.white};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
    `}
`;

const EPCHeaderContent = styled.div`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(33)};
`;

const EPCHeaderSection = styled.section`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};
`;

const EPCHeaderStatusLabel = styled.label`
  font-weight: ${theme.font.weight.semiBold};
`;

const EPCHeaderStatusTag = styled.span`
  position: relative;
  z-index: ${theme.zIndex.content + 1};
  display: flex;
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: fit-content;
  height: ${toRem(20)};
  margin-left: ${toRem(8)};
  padding: 0 ${toRem(5.5)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.white};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(11)};
  line-height: 100%;
  text-transform: capitalize;
  color: ${theme.color.neutral[900]};
  cursor: pointer;

  svg {
    display: flex;
    width: ${toRem(13)};
    height: ${toRem(13)};
    margin-left: ${toRem(3.25)};
    fill: ${theme.color.neutral[200]};
    transition: fill ${theme.transition.icon};
  }

  &:hover {
    svg {
      fill: ${theme.color.neutral[400]};
    }
  }
`;

const EPCHeaderStatusMessage = styled(motion.section)`
  position: relative;
  z-index: ${theme.zIndex.content};
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 130%;
  color: ${theme.color.neutral[900]};
  overflow: hidden;
`;

const EPCHeaderStatusMessageContent = styled.span`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding-bottom: ${toRem(5.5)};
`;

const EPCHeaderDateSection = styled(EPCHeaderSection)<Partial<IEmployeePayoutProps>>`
  font-size: ${toRem(11)};

  ${({ status }) =>
    status === PayoutStatus.CANCELED &&
    css`
      color: ${theme.color.white};
    `}
`;

const EPCHeaderDate = styled.span`
  position: relative;
  top: ${toRem(1)};
`;

const EPCLoaderLabel = <Skeleton width={55} />;
const EPCLoaderContent = <Skeleton width={100} height={13} />;

const TooltipTheme = {
  Dark: {
    default: 'rgba(0,0,0, .44)',
    hover: 'rgba(0,0,0, .66)',
  },
  Light: {
    default: 'rgba(255,255,255,.55)',
    hover: 'rgba(255,255,255,.77)',
  },
};

const EPCHeader = ({ alert, date, isLoading, status, time }: IEmployeePayoutProps) => {
  const [messageVisible, setMessageVisible] = useState<boolean>(false);

  return (
    <EPCHeaderLayout isLoading={isLoading} status={status}>
      <EPCHeaderContent>
        <EPCHeaderSection>
          <EPCHeaderStatusLabel>{isLoading ? <Skeleton width={100} /> : capitalize(status)}</EPCHeaderStatusLabel>

          {alert && !isLoading && (
            <EPCHeaderStatusTag onClick={() => setMessageVisible(!messageVisible)}>
              {alert?.label}
              <Icon icon="Tooltip" />
            </EPCHeaderStatusTag>
          )}
        </EPCHeaderSection>

        <EPCHeaderDateSection status={status}>
          {isLoading ? (
            EPCLoaderContent
          ) : (
            <>
              <TooltipIcon icon="CalendarMinimal" iconFill={status === PayoutStatus.CANCELED ? TooltipTheme.Light : TooltipTheme.Dark} iconMarginLeft={0} iconMarginRight={5.5} iconSize={12} place="left">
                {time}
              </TooltipIcon>

              <EPCHeaderDate>{date}</EPCHeaderDate>
            </>
          )}
        </EPCHeaderDateSection>
      </EPCHeaderContent>

      {alert && (
        <EPCHeaderStatusMessage
          animate={{
            height: messageVisible ? 'auto' : 0,
          }}
          initial={messageVisible}
          transition={{ type: 'tween', duration: 0.125 }}
        >
          <EPCHeaderStatusMessageContent>{alert?.message}</EPCHeaderStatusMessageContent>
        </EPCHeaderStatusMessage>
      )}
    </EPCHeaderLayout>
  );
};

const EPCContent = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(16.5)};
  width: 100%;
  padding: ${toRem(11)};
  background-color: ${theme.color.white};
`;

interface EPCContentRowProps {
  columns?: 1 | 2;
}

const EPCContentRow = styled.div<EPCContentRowProps>`
  display: grid;
  grid-gap: ${toRem(11)};
  width: 100%;

  ${({ columns }) =>
    columns === 1 &&
    css`
      grid-template-columns: 1fr;
    `}

  ${({ columns }) =>
    columns === 2 &&
    css`
      grid-template-columns: repeat(2, 1fr);
    `}
`;

const EPCContentItemLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(8.75)};
  width: 100%;
  color: ${theme.color.neutral[900]};

  label {
    line-height: 100%;
  }

  p {
    width: 100%;
    word-break: break-all;
  }
`;

interface EPCContentItemProps {
  children?: ReactNode;
  isLoading?: boolean;
  label: string;
  value?: string | number;
}

const EPCContentItem = ({ children, isLoading, label, value }: EPCContentItemProps) => (
  <EPCContentItemLayout>
    <Text as="label" size="xxsmall">
      {isLoading ? EPCLoaderLabel : label}
    </Text>

    {value && (
      <Text size="small" fontWeight="semibold">
        {isLoading ? EPCLoaderContent : value}
      </Text>
    )}

    {children && <>{isLoading ? EPCLoaderContent : children}</>}
  </EPCContentItemLayout>
);

export const EmployeePayoutCard = ({ alert, amount, date, employer, isLoading, note, paymentMethod, paymentMethodType, payoutId, status, time, ...rest }: IEmployeePayoutProps) => (
  <EPCItemLayout onClick={() => browserHistory.push(`${routeConstants.private.employeePayoutDetails}/${payoutId}`)} {...rest}>
    <EPCHeader alert={alert} date={date} isLoading={isLoading} status={status} time={time} />

    <EPCContent>
      <EPCContentRow columns={2}>
        <EPCContentItem isLoading={isLoading} label="Amount" value={amount} />
        <EPCContentItem isLoading={isLoading} label="Payment Method" value={undefined}>
          <PaymentMethodAccountNumber align="start" cardType={paymentMethodType} cardNumber={paymentMethod} short fontSize="small" fontWeight="regular" />
        </EPCContentItem>
      </EPCContentRow>

      <EPCContentRow columns={1}>
        <EPCContentItem isLoading={isLoading} label="Employer" value={employer} />
      </EPCContentRow>

      <EPCContentRow columns={1}>
        <EPCContentItem isLoading={isLoading} label="Note" value={note || '--'} />
      </EPCContentRow>
    </EPCContent>
  </EPCItemLayout>
);

// Pending Balance
export const PendingBalance = styled.section`
  position: relative;
  z-index: ${theme.zIndex.contentHeader + 50};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)};
  justify-items: center;
  width: 100%;
  margin-top: ${toRem(-33)};
  margin-bottom: ${toRem(22)};
  padding: 0 ${toRem(theme.layout.gutter.small)} ${toRem(11)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.white};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)};
    margin-top: ${toRem(-22)};
    padding-bottom: ${toRem(theme.layout.gutter.small)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)};
    margin-top: ${toRem(-11)};
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

// PBC === PendingBalanceCard
interface PendingBalanceCardprops {
  bgColor?: string;
  ctaLabel: string;
  ctaTo: string;
  externalTo?: boolean;
  imageAlt: string;
  imageLarge: string;
  imageSmall: string;
  label: string;
  message: string;
  footnote?: string;
}

const PBCLayout = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  min-height: ${toRem(120)};
  border-radius: ${toRem(11)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.white};
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: grid;
    grid-template-columns: ${toRem(150)} 1fr;
    grid-gap: ${toRem(22)};
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    grid-template-columns: minmax(${toRem(184)}, ${toRem(200)}) 1fr;
  }
`;

const PBCImageLayout = styled.div<Partial<PendingBalanceCardprops>>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${toRem(121)};
  background-color: ${theme.color.primary[500]};
  overflow: hidden;

  ${({ bgColor }) =>
    bgColor &&
    css`
      background-color: ${bgColor};
    `}

  img {
    position: relative;
    z-index: 10;
    width: auto;
    height: 100%;
    padding: ${toRem(11)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    height: 100%;
    background-color: transparent;

    img {
      position: absolute;
      width: 100%;
      height: 100%;
      padding: 0;
      object-fit: cover;
      object-position: right center;
    }
  }
`;

const PBCImageLarge = styled.img`
  display: none;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: flex;
  }
`;

const PBCImageSmall = styled.img`
  display: flex;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: none;
  }
`;

const PBCContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(13.75)};
  justify-items: center;
  width: 100%;
  padding: ${toRem(22)};

  @media (min-width: ${theme.breakpoint.med}px) {
    justify-items: start;
  }

  a,
  button {
    flex-grow: 0;
    width: fit-content;
  }
`;

const PBCText = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(5.5)};
  justify-items: center;
  width: 100%;

  label {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.semiBold};
    font-size: ${toRem(16.5)};
    line-height: 120%;
    text-align: center;
    color: ${theme.color.neutral[900]};
  }

  p {
    margin-bottom: 0;
    text-align: center;
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    justify-items: start;

    label,
    p {
      text-align: left;
    }
  }
`;

const ExternalLink = styled.a`
  ${ButtonStyles}
`;

export const PendingBalanceCard = ({ bgColor = theme.color.primary[500], ctaLabel, ctaTo, externalTo, imageAlt, imageLarge, imageSmall, label, message, footnote }: PendingBalanceCardprops) => (
  <PBCLayout data-cy="pending-balance-component">
    <PBCImageLayout bgColor={bgColor}>
      <PBCImageLarge alt={imageAlt} src={imageLarge} />
      <PBCImageSmall alt={imageAlt} src={imageSmall} />
    </PBCImageLayout>

    <PBCContent>
      <PBCText>
        <label>{label}</label>
        <Text size="small" fontWeight="regular">
          {message}
        </Text>
      </PBCText>

      {externalTo ? (
        <ExternalLink appearance="primary" size="small" href={ctaTo} target="_blank" rel="noopener noreferrer">
          {ctaLabel}
        </ExternalLink>
      ) : (
        <Button appearance="primary" size="small" label={ctaLabel} to={ctaTo} />
      )}

      {footnote && (
        <Text size="xxsmall" fontWeight="regular">
          {footnote}
        </Text>
      )}
    </PBCContent>
  </PBCLayout>
);
